import { useEffect, useRef, useState } from "react";
import { formatElapsed } from "./use-recording-elapsed";
import { useTranslation } from "../localization";
import { toast } from "../toast";
import {
	adoptRecoveredRecording,
	canStartRecording,
	type RecordingTarget,
} from "./recorder-session";
import { releaseRecording } from "./recording-claim";
import { persistence } from "./recording-persistence";
import {
	type RecoverableRecording,
	type RecoveryOffer,
	type RecoveryVerdict,
	scanForRecovery,
} from "./recording-recovery";
import { RecordingRecoveryModal } from "./recording-recovery-modal";

/** Props Interface */
export interface RecordingRecoveryHostProps {
	/** `${orgId}:${userId}` of the signed-in session, or null while it is not yet known. */
	owner: string | null;
	/** Asks the server what became of a recording's destination. */
	inspect: (videoId: string) => Promise<RecoveryVerdict>;
	/** Rebuilds the destination a rescued recording ships to. */
	toTarget: (recording: RecoverableRecording) => RecordingTarget;
	/** Takes the destination away, for the author who does not want the recording. */
	remove: (videoId: string) => Promise<unknown>;
}

/**
 * Asks, once per signed-in session, about a recording that never made it off the machine.
 *
 * Everything that touches the API arrives as a prop, the same contract `RecordingTarget` states
 * for the live path: it is what keeps this package free of the API client and of any knowledge
 * about wiki pages.
 *
 * The scan runs behind a ref rather than only an effect dependency, because React runs effects
 * twice in development and a second scan would find the first one's claim and conclude another tab
 * is recording.
 */
export function RecordingRecoveryHost({
	owner,
	inspect,
	toTarget,
	remove,
}: RecordingRecoveryHostProps) {
	/** Context */
	const { t } = useTranslation();

	/** State */
	const [offer, setOffer] = useState<RecoveryOffer | null>(null);
	const scannedFor = useRef<string | null>(null);

	/** Effects */
	useEffect(() => {
		if (!owner || scannedFor.current === owner) return;
		scannedFor.current = owner;
		void scanForRecovery(owner, inspect)
			.then(setOffer)
			.catch((error) => {
				console.error("Looking for an unfinished recording failed:", error);
			});
	}, [owner, inspect]);

	/** Functions */
	function handleOnSend() {
		if (!offer) return;
		// The recorder takes one recording at a time. Clearing the modal on a refusal would leave
		// the author with nothing on screen and the recording still waiting on disk.
		if (!canStartRecording()) {
			toast.error(t("video_recorder.recovery_busy"));
			return;
		}
		// The modal goes now rather than when the upload ends: from here the ordinary card carries
		// the transfer, exactly as it does for a recording that was never lost.
		setOffer(null);
		void adoptRecoveredRecording(
			toTarget(offer.recording),
			offer.chunks,
			offer.recording.durationMs,
			offer.mimeType,
		);
	}

	async function handleOnDelete() {
		if (!offer) return;
		const { videoId } = offer.recording;
		setOffer(null);
		persistence.discard(videoId);
		releaseRecording(videoId);
		// The destination goes with it, block and all. A row whose recording the author threw away
		// would otherwise sit on the page as a placeholder nothing will ever fill.
		await remove(videoId).catch((error) => {
			console.error("Removing the recovered recording's destination failed:", error);
		});
	}

	/** Functions */
	function handleOnClose() {
		// Nothing is destroyed by closing. The record keeps its claim for this session and is
		// offered again at the next start, until it ages out.
		setOffer(null);
	}

	/** Guard */
	if (!offer) return null;

	/** Render */
	return (
		<RecordingRecoveryModal
			label={offer.recording.label}
			duration={formatElapsed(offer.recording.durationMs)}
			isLost={offer.verdict.kind === "unshippable"}
			onSend={handleOnSend}
			onDelete={() => void handleOnDelete()}
			onClose={handleOnClose}
		/>
	);
}
